import { DefaultError, QueryKey } from "@tanstack/query-core";
import { UseQueryResult } from "@tanstack/react-query";

export type QueryHookFn<TData = unknown, TError = DefaultError> = (
  queryResult: UseQueryResult<TData, TError>
) => UseQueryResult<TData, TError>;

export interface QueryHookRegistration<
  TData = unknown,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> {
  queryKey: TQueryKey;
  hook: QueryHookFn<TData, TError>;
  order?: number;
  id?: string;
}

export interface RegisteredQueryHook<
  TData = unknown,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> extends QueryHookRegistration<TData, TError, TQueryKey> {
  order: number;
  serializedQueryKey: string;
}

type AnyRegisteredQueryHook = RegisteredQueryHook<any, any, QueryKey>;

const queryHookRegistry = new Map<string, AnyRegisteredQueryHook[]>();
const queryHookIds = new Map<string, AnyRegisteredQueryHook>();
const emptyQueryHooks: readonly AnyRegisteredQueryHook[] = Object.freeze([]);

export const serializeQueryKey = (queryKey: QueryKey): string => {
  return JSON.stringify(queryKey);
};

const removeRegistration = (registration: AnyRegisteredQueryHook) => {
  const existingHooks = queryHookRegistry.get(registration.serializedQueryKey);
  if (!existingHooks) {
    return;
  }

  const nextHooks = existingHooks.filter((hook) => hook !== registration);
  if (nextHooks.length === 0) {
    queryHookRegistry.delete(registration.serializedQueryKey);
  } else {
    queryHookRegistry.set(registration.serializedQueryKey, nextHooks);
  }

  if (registration.id) {
    queryHookIds.delete(registration.id);
  }
};

export const registerQueryHook = <
  TData = unknown,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
>(
  registration: QueryHookRegistration<TData, TError, TQueryKey>
): (() => void) => {
  const normalizedRegistration: RegisteredQueryHook<TData, TError, TQueryKey> = {
    ...registration,
    order: registration.order ?? 0,
    serializedQueryKey: serializeQueryKey(registration.queryKey),
  };

  if (normalizedRegistration.id) {
    const previousRegistration = queryHookIds.get(normalizedRegistration.id);
    if (previousRegistration) {
      removeRegistration(previousRegistration);
    }
    queryHookIds.set(normalizedRegistration.id, normalizedRegistration);
  }

  const existingHooks = queryHookRegistry.get(normalizedRegistration.serializedQueryKey) ?? [];
  const nextHooks = [ ...existingHooks, normalizedRegistration ].sort((left, right) => {
    return left.order - right.order;
  });

  queryHookRegistry.set(normalizedRegistration.serializedQueryKey, nextHooks);

  return () => {
    removeRegistration(normalizedRegistration);
  };
};

export const getHooksForQueryKey = <
  TData = unknown,
  TError = DefaultError,
>(
  queryKey: QueryKey
): readonly RegisteredQueryHook<TData, TError>[] => {
  const serializedQueryKey = serializeQueryKey(queryKey);
  return (queryHookRegistry.get(serializedQueryKey) as readonly RegisteredQueryHook<TData, TError>[]) ??
    (emptyQueryHooks as readonly RegisteredQueryHook<TData, TError>[]);
};
